import {debounce} from 'underscore';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, PublishingStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Filter, FilterKey, Operator, Sort, SortKey} from 'search/SearchConstants';
import {ChipSearchInputSearch} from 'ui-library/inputs/ChipSearchInput';
import * as net from 'util/Net';

/**
 * The response from the generic search function must be transformed to match
 * the fields from graphql-fragments/__generated/ProcedureSearchAttributesGraphQL.ts
 */
export interface ProcedureSearchQueryResponse {
  id: string;
  name: string;
}

interface ProcedureQueryResponse {
  collectiblesById: {
    [key: string]: {
      creator: {id: string; name: string};
      name: string;
    };
  };
}

const parseProcedureSearchResponse = (resp: ProcedureQueryResponse): Array<ProcedureSearchQueryResponse> =>
  Object.keys(resp.collectiblesById)?.map((procedureId: string) => {
    const procedureRecord = resp.collectiblesById[procedureId];
    return {
      id: procedureId,
      name: procedureRecord.name,
    };
  }) ?? [];

const doProcedureSearchAfterDebounce: ChipSearchInputSearch = (
  url: string,
  query: string,
  params: Object,
  callbacks: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    done: (resp: any, status: any, xhr: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fail: (resp: any) => void;
    always: () => void;
  }
): void => {
  net
    .postJSON({url, data: {query, ...params}})
    .done((resp: ProcedureQueryResponse, status: unknown, xhr: unknown) => {
      const parsedResponse = parseProcedureSearchResponse(resp);
      callbacks.done({data: parsedResponse}, status, xhr);
    })
    .fail(callbacks.fail)
    .always(callbacks.always);
};

const DEBOUNCE_MS = 250;

export const procedureSearchUrl: string = '/1/api/search';

export const doProcedureSearch: ChipSearchInputSearch = debounce(doProcedureSearchAfterDebounce, DEBOUNCE_MS);

export const procedureSearchFilters = (procedureTypes?: Array<ProcedureType>): Array<Filter> => {
  const filters: Array<Filter> = [
    {
      key: FilterKey.ARCHIVE_PURPOSES,
      operator: Operator.IS_ONE_OF,
      value: [NOT_ARCHIVED_PURPOSE_STRING],
    },
  ];

  if (BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING) {
    filters.push({
      key: FilterKey.PUBLISHING_STATUS,
      operator: Operator.IS,
      value: PublishingStatus.EFFECTIVE,
    });
  }

  const typeValueMap = {
    [ProcedureType.RECIPE]: ModelTypeConstants.RECIPE_PROCEDURE,
    [ProcedureType.ASSAY]: ModelTypeConstants.ASSAY_PROCEDURE,
  };

  const typeValues = procedureTypes?.map((type) => typeValueMap[type]) ?? [];
  if (typeValues.length > 0) {
    filters.push({
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: typeValues,
    });
  }

  return filters;
};

export const procedureSearchSorts: Array<Sort> = [
  {
    key: SortKey.SORT_NAME_KEY,
    reverse: false,
  },
];
